/**
 * ADR 0234 — the PURE core of the app's calendar-day language. Zero imports,
 * zero aliases, ON PURPOSE: the e2e suites import this file relatively
 * (Playwright cannot resolve the `@/` alias that `date.ts` uses), so the app
 * and its tests read "today" from the same single source instead of each
 * keeping a copy.
 *
 * TZ is pinned to Asia/Bangkok — the server's day language (`TICKET_TZ`,
 * `JOB-YYYYMMDD-…` day prefixes, the War Room service day). "Today" here
 * always matches the data's buckets, whatever a user's machine is set to.
 * This deliberately supersedes ADR 0223 D3's browser-timezone choice.
 * If per-org timezones ever land (exit criterion in ADR 0234: the first
 * non-Thai org, full-stack in ONE ADR), this const is the web's single
 * injection point.
 *
 * `date.ts` re-exports everything here and layers the locale-aware,
 * UI-facing formatters on top. Never write `toISOString().slice(0, 10)`
 * outside these two files — ADR 0234's grep gate goes red.
 */

export const TZ = "Asia/Bangkok";

export type DateInput = string | Date | null | undefined;

export function toDate(d: DateInput): Date | null {
  if (d == null || d === "") return null;
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
}

// Machine-readable, deliberately locale-independent: this value is compared,
// stored and put into <input type="date">, so it must not follow the UI.
// en-CA is the locale whose default date pattern IS YYYY-MM-DD.
const isoFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** YYYY-MM-DD anchored to Asia/Bangkok. Replaces `.slice(0, 10)` on UTC ISO strings. */
export function formatDateISO(d: DateInput, placeholder = "—"): string {
  const dt = toDate(d);
  return dt ? isoFmt.format(dt) : placeholder;
}

/**
 * DD-MM-YYYY anchored to Asia/Bangkok — the format the vehicle screens read in.
 *
 * ADR 0350 — those screens used to show three formats at once: `2027-03-31`
 * from the ISO helper, `01 ก.ค. 2569` from the locale one (Thai locale means
 * Intl hands back the BUDDHIST year), and raw `.slice(0, 10)` off the API
 * string. Two of them sat in neighbouring columns of the same row, 543 years
 * apart, and nothing on screen said which was which.
 *
 * The year stays GREGORIAN. Day-month order is what was asked for; switching
 * era as well would silently move every date on the screen by 543 years, which
 * is a different decision from a formatting one.
 *
 * Built from the ISO parts rather than a `dd-MM-yyyy` Intl pattern so the
 * separator and the era can never drift with the locale.
 */
export function formatDateDMY(d: DateInput, placeholder = "—"): string {
  const iso = formatDateISO(d, "");
  if (!iso) return placeholder;
  const [y, m, day] = iso.split("-");
  return `${day}-${m}-${Number(y) + yearOffset}`;
}

/**
 * The organisation's year for the one display format built here (`formatDateDMY`): 0 for ค.ศ., 543 for พ.ศ. — set by
 * `setYearEra` (display-locale.ts). Kept as a plain number so this file stays import-free. Machine values
 * (`formatDateISO`, `todayISO`) never follow it.
 */
let yearOffset = 0;
export function setYearOffset(n: number): void {
  yearOffset = n;
}

/** Today as YYYY-MM-DD in Asia/Bangkok. Replaces `new Date().toISOString().slice(0, 10)`. */
export function todayISO(): string {
  return isoFmt.format(new Date());
}
